import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { BRAND } from '../lib/constants';
import styles from './LegalPage.module.css';

export const PrivacyPolicyPage = () => {
  const { i18n } = useTranslation();
  const isIt = i18n.language === 'it';

  useDocumentMetadata(
    isIt ? 'Privacy Policy — NexTrainE' : 'Privacy Policy — NexTrainE',
    isIt ? 'Informativa sulla privacy di NexTrainE S.r.l.' : 'NexTrainE S.r.l. privacy policy.'
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Link to="/" className={styles.backLink}>
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {isIt ? 'Torna alla Home' : 'Back to Home'}
        </Link>

        <p className={styles.lastUpdate}>
          {isIt ? 'Ultimo aggiornamento: 2 luglio 2026' : 'Last updated: July 2, 2026'}
        </p>
        <h1 className={styles.title}>Privacy Policy</h1>

        <div className={styles.content}>
          {isIt ? <PrivacyIT /> : <PrivacyEN />}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────── ITALIAN ─────────────────── */
const PrivacyIT = () => (
  <>
    <h2>1. Titolare del Trattamento</h2>
    <p>
      Il Titolare del trattamento dei dati personali è <strong>{BRAND.legal}</strong>,
      con sede in {BRAND.address}, {BRAND.city}
      {' '}({BRAND.vat}).
    </p>
    <p>
      Per qualsiasi richiesta relativa alla presente informativa, è possibile
      contattare il Titolare all'indirizzo email:{' '}
      <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
    </p>

    <hr className={styles.divider} />

    <h2>2. Dati Raccolti</h2>
    <p>
      Il presente sito web è di natura informativa e <strong>non raccoglie direttamente
      dati personali</strong> degli utenti tramite form di registrazione, account o moduli
      di contatto. La sezione "Contatti" apre il client email dell'utente, senza che
      i dati vengano processati dal sito.
    </p>

    <h3>2.3 Dati raccolti tramite cookie analitici</h3>
    <p>
      Il sito utilizza <strong>Google Analytics 4</strong> (Google LLC) per raccogliere
      statistiche anonime sull'utilizzo del sito. I dati raccolti includono: pagine
      visitate, durata della sessione, tipo di dispositivo e browser, paese di
      provenienza e fonte di traffico. Gli indirizzi IP sono anonimizzati da Google
      prima della memorizzazione.
    </p>

    <h3>2.1 Dati di navigazione</h3>
    <p>
      I sistemi informatici e le procedure software preposte al funzionamento del
      sito acquisiscono, nel normale esercizio, alcuni dati personali la cui
      trasmissione è implicita nell'uso dei protocolli di comunicazione Internet
      (es. indirizzi IP, nomi a dominio, orari delle richieste). Tali dati, non
      raccolti per essere associati a utenti identificati, potrebbero — attraverso
      elaborazioni ed associazioni con dati detenuti da terzi — permettere di
      identificare gli utenti.
    </p>

    <h3>2.2 Dati tecnici memorizzati localmente</h3>
    <p>
      Il sito utilizza <strong>localStorage</strong> del browser per memorizzare la
      preferenza linguistica dell'utente (italiano/inglese). Questo dato è
      strettamente tecnico, non viene trasmesso a server esterni e non costituisce
      un cookie.
    </p>

    <hr className={styles.divider} />

    <h2>3. Servizi di Terze Parti</h2>

    <h3>3.1 Google Analytics 4</h3>
    <p>
      Il sito utilizza <strong>Google Analytics 4</strong> (Google LLC), un servizio
      di analisi web che utilizza cookie per raccogliere informazioni statistiche
      aggregate sull'utilizzo del sito. I cookie utilizzati sono <code>_ga</code> e{' '}
      <code>_ga_&lt;ID&gt;</code>. I dati sono trattati in forma anonima e aggregata.
    </p>
    <p>
      L'utente può disattivare Google Analytics installando il componente aggiuntivo
      per il browser:{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
        tools.google.com/dlpage/gaoptout
      </a>.
    </p>
    <p>
      Informativa di Google:{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
        policies.google.com/privacy
      </a>.
    </p>

    <h3>3.2 Google Fonts</h3>
    <p>
      Il sito carica font tipografici da <strong>Google Fonts</strong> (Google LLC).
      Quando si accede al sito, il browser dell'utente stabilisce una connessione
      diretta ai server di Google per scaricare i font. In questa operazione, Google
      riceve l'indirizzo IP dell'utente. Google Fonts non utilizza cookie.
    </p>

    <h3>3.3 Unsplash</h3>
    <p>
      Alcune immagini illustrative sono caricate da <strong>Unsplash</strong>. In
      fase di caricamento, il browser stabilisce una connessione ai server di
      Unsplash, che può ricevere l'indirizzo IP dell'utente.
    </p>

    <h3>3.4 Amazon CloudFront</h3>
    <p>
      Il sito è distribuito tramite la rete CDN <strong>Amazon CloudFront</strong>
      {' '}(Amazon Web Services, Inc.). I log di accesso standard possono contenere
      indirizzi IP e informazioni sulla richiesta.
    </p>

    <hr className={styles.divider} />

    <h2>4. Base Giuridica del Trattamento</h2>
    <p>
      Il trattamento dei dati di navigazione è basato sul <strong>legittimo interesse</strong>
      {' '}del Titolare (Art. 6.1.f GDPR) al corretto funzionamento del sito web.
      La memorizzazione della preferenza linguistica è basata sul legittimo interesse
      alla fruibilità del sito.
    </p>
    <p>
      L'utilizzo dei cookie analitici di Google Analytics è basato sul{' '}
      <strong>consenso esplicito</strong> dell'utente (Art. 6.1.a GDPR), raccolto
      tramite il banner di consenso cookie al primo accesso al sito. L'utente può
      revocare il consenso in qualsiasi momento tramite l'apposita sezione nella
      pagina Cookie Policy o cancellando i dati del sito dal browser. Senza
      consenso, nessun cookie analitico viene installato.
    </p>

    <hr className={styles.divider} />

    <h2>5. Conservazione dei Dati</h2>
    <p>
      I dati di navigazione vengono cancellati immediatamente dopo l'elaborazione
      statistica in forma anonima. La preferenza linguistica resta nel browser
      dell'utente finché non viene cancellata manualmente.
    </p>

    <hr className={styles.divider} />

    <h2>6. Diritti dell'Interessato</h2>
    <p>
      Ai sensi degli articoli 15–22 del Regolamento (UE) 2016/679 (GDPR),
      l'interessato ha il diritto di:
    </p>
    <ul>
      <li>Accedere ai propri dati personali (Art. 15)</li>
      <li>Ottenere la rettifica dei dati inesatti (Art. 16)</li>
      <li>Ottenere la cancellazione dei dati ("diritto all'oblio") (Art. 17)</li>
      <li>Limitare il trattamento (Art. 18)</li>
      <li>Portabilità dei dati (Art. 20)</li>
      <li>Opporsi al trattamento (Art. 21)</li>
      <li>Proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali
        (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>)
      </li>
    </ul>
    <p>
      Per esercitare i propri diritti, l'utente può scrivere a{' '}
      <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
    </p>

    <hr className={styles.divider} />

    <h2>7. Trasferimento dei Dati Extra-UE</h2>
    <p>
      Alcuni servizi di terze parti (Google Analytics, Google Fonts, Amazon CloudFront)
      possono comportare il trasferimento di dati verso server situati negli Stati
      Uniti. Tali trasferimenti avvengono sulla base delle clausole contrattuali
      standard (SCC) adottate dalla Commissione Europea e/o del Data Privacy
      Framework UE-USA.
    </p>

    <hr className={styles.divider} />

    <h2>8. Modifiche alla presente Informativa</h2>
    <p>
      Il Titolare si riserva il diritto di apportare modifiche alla presente
      informativa in qualsiasi momento. La data di ultimo aggiornamento è indicata
      in cima al documento. Si invita l'utente a consultare periodicamente questa
      pagina.
    </p>
  </>
);

/* ─────────────────── ENGLISH ─────────────────── */
const PrivacyEN = () => (
  <>
    <h2>1. Data Controller</h2>
    <p>
      The Data Controller is <strong>{BRAND.legal}</strong>, located at{' '}
      {BRAND.address}, {BRAND.city} ({BRAND.vat}).
    </p>
    <p>
      For any inquiries regarding this policy, you may contact the Data Controller
      at: <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
    </p>

    <hr className={styles.divider} />

    <h2>2. Data Collected</h2>
    <p>
      This website is informational and <strong>does not directly collect personal
      data</strong> through registration forms, accounts, or contact forms. The
      "Contact" section opens the user's email client without any data being
      processed by the website.
    </p>

    <h3>2.3 Data collected via analytics cookies</h3>
    <p>
      The site uses <strong>Google Analytics 4</strong> (Google LLC) to collect
      anonymous statistics about website usage. Data collected includes: pages
      visited, session duration, device type and browser, country of origin, and
      traffic source. IP addresses are anonymized by Google before storage.
    </p>

    <h3>2.1 Browsing data</h3>
    <p>
      The computer systems and software procedures used to operate this website
      acquire, during their normal operation, certain personal data whose
      transmission is implicit in the use of Internet communication protocols
      (e.g., IP addresses, domain names, request times). Such data — while not
      collected to be associated with identified users — could, through processing
      and association with data held by third parties, allow user identification.
    </p>

    <h3>2.2 Locally stored technical data</h3>
    <p>
      The site uses the browser's <strong>localStorage</strong> to store the user's
      language preference (Italian/English). This is strictly technical data, is not
      transmitted to external servers, and does not constitute a cookie.
    </p>

    <hr className={styles.divider} />

    <h2>3. Third-Party Services</h2>

    <h3>3.1 Google Analytics 4</h3>
    <p>
      The site uses <strong>Google Analytics 4</strong> (Google LLC), a web analytics
      service that uses cookies to collect aggregate statistical information about
      website usage. The cookies used are <code>_ga</code> and{' '}
      <code>_ga_&lt;ID&gt;</code>. Data is processed in anonymous, aggregate form.
    </p>
    <p>
      Users can opt out of Google Analytics by installing the browser add-on:{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
        tools.google.com/dlpage/gaoptout
      </a>.
    </p>
    <p>
      Google's privacy policy:{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
        policies.google.com/privacy
      </a>.
    </p>

    <h3>3.2 Google Fonts</h3>
    <p>
      The site loads typefaces from <strong>Google Fonts</strong> (Google LLC). When
      you access the site, your browser establishes a direct connection to Google's
      servers to download fonts. During this process, Google receives the user's IP
      address. Google Fonts does not use cookies.
    </p>

    <h3>3.3 Unsplash</h3>
    <p>
      Some illustrative images are loaded from <strong>Unsplash</strong>. When
      loading, the browser connects to Unsplash servers, which may receive the
      user's IP address.
    </p>

    <h3>3.4 Amazon CloudFront</h3>
    <p>
      The site is distributed via <strong>Amazon CloudFront</strong> CDN (Amazon Web
      Services, Inc.). Standard access logs may contain IP addresses and request
      information.
    </p>

    <hr className={styles.divider} />

    <h2>4. Legal Basis for Processing</h2>
    <p>
      The processing of browsing data is based on the <strong>legitimate
      interest</strong> of the Data Controller (Art. 6.1.f GDPR) in the proper
      functioning of the website. The storage of language preference is based on the
      legitimate interest in website usability.
    </p>
    <p>
      The use of Google Analytics cookies is based on the{' '}
      <strong>explicit consent</strong> of the user (Art. 6.1.a GDPR), collected
      via the cookie consent banner upon first visit. Users may withdraw consent
      at any time via the dedicated section on the Cookie Policy page or by
      clearing the site's data from their browser. Without consent, no analytics
      cookies are installed.
    </p>

    <hr className={styles.divider} />

    <h2>5. Data Retention</h2>
    <p>
      Browsing data is deleted immediately after anonymous statistical processing.
      The language preference remains in the user's browser until manually cleared.
    </p>

    <hr className={styles.divider} />

    <h2>6. User Rights</h2>
    <p>
      Under Articles 15–22 of Regulation (EU) 2016/679 (GDPR), users have the right
      to:
    </p>
    <ul>
      <li>Access their personal data (Art. 15)</li>
      <li>Rectify inaccurate data (Art. 16)</li>
      <li>Erase their data ("right to be forgotten") (Art. 17)</li>
      <li>Restrict processing (Art. 18)</li>
      <li>Data portability (Art. 20)</li>
      <li>Object to processing (Art. 21)</li>
      <li>Lodge a complaint with the Italian Data Protection Authority
        (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>)
      </li>
    </ul>
    <p>
      To exercise your rights, contact us at{' '}
      <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a>.
    </p>

    <hr className={styles.divider} />

    <h2>7. Data Transfer Outside the EU</h2>
    <p>
      Some third-party services (Google Analytics, Google Fonts, Amazon CloudFront)
      may involve data transfers to servers located in the United States. Such
      transfers are carried out on the basis of Standard Contractual Clauses (SCCs)
      adopted by the European Commission and/or the EU-US Data Privacy Framework.
    </p>

    <hr className={styles.divider} />

    <h2>8. Changes to This Policy</h2>
    <p>
      The Data Controller reserves the right to amend this policy at any time. The
      last update date is indicated at the top of this document. Users are encouraged
      to periodically review this page.
    </p>
  </>
);
